import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Progressive text reveal for AI-generated copy.
 *
 * Some ChatGPT drawers land their whole answer in one frame, which is the one
 * moment those features look least like they did any work: the adviser waits on
 * a blank box, then a wall of text appears. Revealing it turns the same wait
 * into something to watch, and — because the drawers are used in front of
 * clients — makes the generation legible as generation.
 *
 * The others STREAM. `openaiGenerateWithTextStreaming` (store/openai/api.js)
 * calls its setter once per network chunk with the answer so far, so `text`
 * grows in place — five of ContentBox's seven callers are wired that way
 * (ChatGPTFunDrawer, ChatGPTIceBreakingDrawerV2, ChatGPTIGPostDrawer twice,
 * ChatGPTPersonalMessageDrawer). Growing text is therefore treated as MORE OF
 * THE SAME answer and the reveal continues from what is already on screen; only
 * a value that is not an extension of the last one starts over. Without that
 * distinction every chunk reset the reveal to zero characters, so the box blanked
 * and re-typed for the whole generation and the real reveal only began after the
 * last chunk had landed.
 *
 * Which also means the reveal does the useful thing in both worlds: when the
 * stream outruns `cps` it paces the answer, and when the stream is the slower of
 * the two it simply tracks it. What it never does is go backwards.
 *
 * Timed off elapsed milliseconds and rAF rather than a per-character
 * `setInterval`, so the reveal takes the same wall-clock time on a slow device
 * as a fast one (an interval starves under load and stretches a 3s reveal into
 * something nobody waits for). Same reason `useCountUp` in ClientV2/StatTiles
 * is written this way; this is the text-shaped version of that.
 */

export type TypewriterOptions = {
  /** Off entirely — the caller gets `text` straight through. */
  enabled?: boolean;
  /** Characters per second. Tuned for mixed CJK/Latin drawer copy. */
  cps?: number;
  /** Ceiling on the whole reveal, so a long IG post cannot outstay its welcome. */
  maxMs?: number;
};

export type TypewriterState = {
  /** What to render right now. Equal to `text` once the reveal has finished. */
  shown: string;
  /** True only while characters are still arriving. */
  isTyping: boolean;
  /** Jump to the full text immediately — the skip affordance. */
  reveal: () => void;
};

/**
 * 34 characters/second. Chosen for Traditional Chinese, which is what most of
 * this copy is: a CJK character carries roughly a whole word, so the Latin
 * rates that feel brisk (60-80cps) read as a blur here, and anything under ~25
 * reads as a stall. The cap then bounds the worst case — an IG post can run
 * past 600 characters, and 18 seconds of typing is a feature nobody uses twice.
 */
const DEFAULT_CPS = 34;
const DEFAULT_MAX_MS = 4200;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useTypewriter = (text: unknown, options: TypewriterOptions = {}): TypewriterState => {
  const { enabled = true, cps = DEFAULT_CPS, maxMs = DEFAULT_MAX_MS } = options;

  // Anything that is not a string is passed through untouched. ContentBox is
  // shared by six drawers and nothing stops one of them handing it a node; a
  // reveal that assumed `.slice` would take that drawer down at render.
  const full = typeof text === 'string' ? text : '';
  const passthrough = typeof text !== 'string' || !enabled || full.length === 0;

  const [count, setCount] = useState(passthrough ? full.length : 0);
  const rafRef = useRef(0);
  // Lets `reveal()` stop an in-flight animation without being re-created on
  // every frame (it is handed to an onClick, and a new identity each frame
  // would churn the handler).
  const doneRef = useRef(false);
  // What is on screen as the effect runs. A ref rather than reading `count`,
  // which would have to become a dependency below and would then restart the
  // reveal on every frame it sets.
  const countRef = useRef(count);
  countRef.current = count;
  // The value the LAST RUN of the effect animated — not the last render's, which
  // is the same thing on every frame. This is what an append is measured against.
  const previousFullRef = useRef('');
  // A skip covers the rest of the answer it was asked for, chunks included.
  // Without it the next chunk of a streaming answer would start typing again
  // over a reveal the adviser had explicitly dismissed.
  const skippedRef = useRef(false);

  useEffect(() => {
    doneRef.current = false;

    // Text that EXTENDS what was there is the same answer arriving in pieces.
    // `previous !== ''` because every string starts with the empty one, and the
    // drawers clear `text` before each generation — treating that as an append
    // would carry a finished reveal into the next answer.
    const previous = previousFullRef.current;
    const isAppend = previous !== '' && full !== previous && full.startsWith(previous);
    previousFullRef.current = full;
    if (!isAppend) skippedRef.current = false;

    if (passthrough || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      setCount(full.length);
      doneRef.current = true;
      return undefined;
    }

    // Already dismissed: later chunks land whole rather than re-typing.
    if (skippedRef.current) {
      setCount(full.length);
      doneRef.current = true;
      return undefined;
    }

    // Resume from what is on screen when this is more of the same answer, from
    // nothing when it is a different one.
    const from = isAppend ? Math.min(countRef.current, full.length) : 0;
    const duration = Math.min(maxMs, (full.length / Math.max(1, cps)) * 1000);
    // Back-dated so `tick`'s first frame reads `from` again: the reveal picks up
    // where it was instead of rewinding, and keeps running at `cps` from there.
    const start = performance.now() - (full.length > 0 ? (from / full.length) * duration : 0);
    setCount(from);

    const tick = (now: number) => {
      if (doneRef.current) return;
      const progress = duration <= 0 ? 1 : Math.min(1, (now - start) / duration);
      setCount(Math.round(full.length * progress));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
      else doneRef.current = true;
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [full, passthrough, cps, maxMs]);

  const reveal = useCallback(() => {
    doneRef.current = true;
    skippedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    setCount(full.length);
  }, [full.length]);

  // `count` can lead `full` by a frame when the text changes: the effect that
  // resets it has not run yet, so slicing on the raw count would be a no-op
  // here but is a real out-of-range read the moment the new text is shorter.
  const shown = typeof text === 'string' ? full.slice(0, Math.min(count, full.length)) : '';

  return {
    shown: typeof text === 'string' ? shown : (text as string),
    isTyping: !passthrough && count < full.length,
    reveal
  };
};

export default useTypewriter;
